import { useEffect } from 'react'
import toast from 'react-hot-toast'
import { useLocation } from 'react-router'

import {
  flattenPages,
  getNextPageParam,
  useInfiniteQuery,
} from '@fv/client-core'
import type { PagedShipments, ShipmentFilter } from '@fv/client-types'

import { defaultPageSize } from '@/constants'
import { shipperFetch } from '@/utils/shipperFetch'
import { normalizeQueryString } from '@/utils/stringTransforms'

import { defaultSortBy, defaultSortDirection } from './useShipmentListParams'
import { usePendingSortDirectionPreference } from './useSortDirectionPreference'

export const inProcessShipmentKeys = {
  all: ['shipments', 'inProcess'] as const,
  allRecentTypes: () => ['shipment-recent-types'] as const,
  recentType: (type: ShipmentFilter['recentType']) => [
    ...inProcessShipmentKeys.allRecentTypes(),
    type,
  ],
  filtered: (qs: string) =>
    [...inProcessShipmentKeys.all, normalizeQueryString(qs, ['load'])] as const,
} as const

export const fetchInProcessShipments = (
  qs: string,
): Promise<PagedShipments> => {
  return shipperFetch(`/shipments/in-process?${qs}`)
}
type Opts = {
  enabled?: boolean
}

export function useInProcessShipments({ enabled = true }: Opts = {}) {
  const [preferredSortDirection] = usePendingSortDirectionPreference()
  const { search } = useLocation()
  const params = new URLSearchParams(search)

  if (!params.get('sortBy')) params.set('sortBy', defaultSortBy)
  if (!params.get('sortDirection')) {
    params.set(
      'sortDirection',
      (preferredSortDirection ?? defaultSortDirection).toString(),
    )
  }

  const shipmentsQuery = useInfiniteQuery({
    queryKey: inProcessShipmentKeys.filtered(params.toString()),
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      params.set('limit', String(defaultPageSize))
      params.set('skip', String(pageParam))

      return fetchInProcessShipments(params.toString())
    },
    getNextPageParam,
    enabled,
  })

  // Handle invalid filters
  useEffect(() => {
    if (
      shipmentsQuery.error instanceof Error &&
      shipmentsQuery.error.name === 'EventValidationError'
    ) {
      toast.error(shipmentsQuery.error.message)
    }
  }, [shipmentsQuery.error])

  return {
    ...shipmentsQuery,
    data: flattenPages(shipmentsQuery.data?.pages),
  }
}
